import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";

import { LocaleCombobox } from "@/components/locale/combobox";
import { BrandIcon } from "@/components/ui/brand-icon";
import { Button } from "@/components/ui/button";
import { useAuthSession } from "@/hooks/use-auth-session";
import { getMultiResumePublicChromeCopy, multiResumeBrand } from "@/utils/multiresume-brand";

import { PublicFooter } from "./public-footer";

type LegalPageProps = {
  title: string;
  description: string;
  children: React.ReactNode;
};

export function LegalPage({ title, description, children }: LegalPageProps) {
  const { i18n } = useLingui();
  const session = useAuthSession();
  const publicChromeCopy = getMultiResumePublicChromeCopy();
  const authCta = session
    ? { href: "/dashboard/resumes", label: <Trans>Go to dashboard</Trans> }
    : { href: "/auth/login", label: <Trans>Sign in</Trans> };

  return (
    <main className="min-h-svh bg-background">
      <header className="border-b border-border">
        <nav className="container mx-auto flex items-center gap-4 px-4 py-3 sm:px-6 lg:px-12">
          <a href="/" className="transition-opacity hover:opacity-80" aria-label={i18n._("MultiResume home")}>
            <BrandIcon className="h-9 w-auto" />
          </a>

          <div className="ml-auto flex items-center gap-2">
            <div aria-label={i18n._("Change language")}>
              <LocaleCombobox ordinaryOnly className="h-9 w-36 sm:w-44" />
            </div>
            <Button
              variant="ghost"
              nativeButton={false}
              className="hidden sm:inline-flex"
              render={<a href="/terms#support">{publicChromeCopy.support}</a>}
            />
            <Button nativeButton={false} render={<a href={authCta.href}>{authCta.label}</a>} />
          </div>
        </nav>
      </header>

      <article className="container mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-12">
        <div className="mb-10 space-y-3 border-b border-border pb-8">
          <p className="text-sm font-medium text-primary">{multiResumeBrand.appName}</p>
          <h1 className="text-3xl font-semibold tracking-tight sm:text-5xl">{title}</h1>
          <p className="max-w-2xl text-base leading-relaxed text-muted-foreground">{description}</p>
        </div>

        <div className="space-y-10 text-sm leading-relaxed text-muted-foreground [&_a]:font-medium [&_a]:text-foreground [&_a]:underline [&_a]:underline-offset-4 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:tracking-tight [&_h2]:text-foreground [&_h3]:font-semibold [&_h3]:text-foreground [&_li]:ml-5 [&_ol]:list-decimal [&_p]:mt-3 [&_ul]:list-disc">
          {children}
        </div>
      </article>

      <PublicFooter />
    </main>
  );
}
